import { parse as parseExif } from 'exifr';

import { HarvestProofPhotoExif } from '@harvest-proof/data-access';

function formatExifDate(value: unknown): string | null {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  return null;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return null;
}

interface ExtractClientPhotoExifOptions {
  captureLatitude?: number;
  captureLongitude?: number;
}

/**
 * Read EXIF from the original file before canvas conversion strips it.
 * Falls back to capture GPS + file info when the browser removed EXIF.
 */
export async function extractClientPhotoExif(
  file: File,
  options: ExtractClientPhotoExifOptions = {},
): Promise<HarvestProofPhotoExif> {
  const extractedAt = new Date().toISOString();
  const base: HarvestProofPhotoExif = {
    extracted_at: extractedAt,
    source: 'client',
    file_name: file.name || null,
    file_type: file.type || null,
    file_size: file.size || null,
  };

  try {
    const parsed = await parseExif(file, {
      exif: true,
      gps: true,
      tiff: true,
      reviveValues: true,
      pick: [
        'DateTimeOriginal',
        'DateTimeDigitized',
        'Make',
        'Model',
        'Orientation',
        'latitude',
        'longitude',
        'GPSAltitude',
      ],
    });

    if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
      const latitude = toNumber(parsed.latitude);
      const longitude = toNumber(parsed.longitude);

      return {
        ...base,
        datetime_original: formatExifDate(parsed.DateTimeOriginal),
        datetime_digitized: formatExifDate(parsed.DateTimeDigitized),
        make: typeof parsed.Make === 'string' ? parsed.Make : null,
        model: typeof parsed.Model === 'string' ? parsed.Model : null,
        orientation: toNumber(parsed.Orientation),
        gps:
          latitude != null && longitude != null
            ? {
                latitude,
                longitude,
                altitude: toNumber(parsed.GPSAltitude),
              }
            : null,
      };
    }
  } catch {
    // Browser may block EXIF on camera captures — fall through to fallback.
  }

  const captureLatitude = toNumber(options.captureLatitude);
  const captureLongitude = toNumber(options.captureLongitude);

  return {
    ...base,
    missing: true,
    capture_gps:
      captureLatitude != null && captureLongitude != null
        ? {
            latitude: captureLatitude,
            longitude: captureLongitude,
            altitude: null,
          }
        : null,
  };
}
